import type { EVMAddress } from "@opensea/utils"
import { useCallback } from "react"
import type { Hex } from "viem"
import { useSignTypedData } from "wagmi"
import { useRequireChain } from "../../hooks/useRequireChain"
import { parseSignatureError } from "../../lib/errors"
import type { SignTypedFunction } from "../../types"
import { useWithConnectorErrorRetry } from "./useWithConnectorErrorRetry"

export const useEvmSignTypedFunction = () => {
  const { requireChain } = useRequireChain()
  const { signTypedDataAsync } = useSignTypedData()

  const withConnectorErrorRetry = useWithConnectorErrorRetry()

  const signTyped: SignTypedFunction = useCallback(
    async ({ address, domain, message, primaryType, types }) => {
      try {
        const signature: Hex = await withConnectorErrorRetry(async () => {
          await requireChain(domain.chainId as number)
          return await signTypedDataAsync({
            domain,
            message,
            primaryType,
            types,
            account: address as EVMAddress,
          })
        })

        // Version of signature should be 27 or 28 for Ethereum signatures, but 0 and 1
        // are also possible versions returned, ie from Ledger signing that need to be adapted
        let v = Number.parseInt(signature.slice(-2), 16)

        if (v < 27) {
          v += 27
        }

        const normalizedSignature = signature.slice(0, -2) + v.toString(16)

        return normalizedSignature
      } catch (err: unknown) {
        const error = err as Error
        const externalError = parseSignatureError(error)
        throw externalError
      }
    },
    [requireChain, signTypedDataAsync, withConnectorErrorRetry],
  )

  return signTyped
}
